import { useEffect, useRef, useCallback, useState } from 'react';
import maplibregl, { Map as MapLibreMap, NavigationControl, ScaleControl } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useMapStore, BUNDLED_FALLBACK_STYLE } from '@/stores/mapStore';
import { useUIStore } from '@/stores/uiStore';
import { useRoutingStore } from '@/stores/routingStore';
import { useSearchStore } from '@/stores/searchStore';
import { searchService } from '@/services/searchService';
import type { SearchResult, PlaceCategory, Address } from '@/types';
import { MapControls } from './MapControls';
import { ShareLayer } from '@/components/share';
import { MapLayers } from './MapLayers';
import { MapMarkers } from './MapMarkers';
import { TrafficLayer } from './TrafficLayer';
import { IsochroneLayer } from './IsochroneLayer';
import { useIsochroneStore } from './isochroneStore';
import { MapContextMenu } from './MapContextMenu';
import { ReportHazard } from './ReportHazard';
import { GeoChat } from './GeoChat';
import { LocationPanel } from '@/components/location/LocationPanel';
import { LocationPermissionPrompt } from '@/components/location/LocationPermissionPrompt';
import { useGeolocation } from '@/hooks/useGeolocation';
import config from '@/config';

// Initial map style: prefer the runtime/env-configured style URL (so it points at
// the right tileserver host per environment), falling back to the bundled local style.
const DEFAULT_STYLE = config.map.styles.default || BUNDLED_FALLBACK_STYLE;

export function MapContainer() {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);

  const {
    viewport,
    setMap,
    setIsLoaded,
    setViewport,
    show3DBuildings,
    showTerrain,
  } = useMapStore();

  const { preferences } = useUIStore();
  const activeWaypointId = useRoutingStore((s) => s.activeWaypointId);
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; lat: number; lng: number } | null>(null);

  // Auto-start location on load so the current-location marker shows by default
  // (prompts once for first-time visitors; silent for those who already granted).
  const geo = useGeolocation({ autoStart: true, flyToOnFirstFix: true });
  // Keep a ref to the hook so the nav effect can call start/stopTracking without
  // depending on their (per-render unstable) identities.
  const geoRef = useRef(geo);
  geoRef.current = geo;

  // Turn-by-turn navigation needs a continuous position feed. useGeolocation is
  // autoStart:false (browsers block non-gesture location requests), so without
  // this NavigationMode would hang on "Preparing navigation…" if the user never
  // tapped Locate. Start tracking when navigation activates; stop it on nav end
  // unless the user was already tracking before they started navigating.
  const navActive = useRoutingStore((s) => s.navigation.isActive);
  const wasTrackingBeforeNavRef = useRef(false);
  useEffect(() => {
    if (navActive) {
      wasTrackingBeforeNavRef.current = useMapStore.getState().isTrackingLocation;
      if (!wasTrackingBeforeNavRef.current) {
        geoRef.current.startTracking();
      }
    } else if (!wasTrackingBeforeNavRef.current) {
      geoRef.current.stopTracking();
    }
  }, [navActive]);

  // "Follow me" mode (engaged from the Locate control). Keeps the camera centred
  // on the live GPS position as the user moves — the piece that made the map feel
  // static while walking/driving. Turn-by-turn navigation has its own richer
  // follow (with heading), so this general follow stands down while nav is active.
  const isFollowingUser = useMapStore((s) => s.isFollowingUser);
  const followUserLocation = useMapStore((s) => s.userLocation);
  const storeMap = useMapStore((s) => s.map);

  // Engaging follow needs a continuous position feed; start the watch if the user
  // wasn't already tracking (the tap is the required user gesture for iOS/Android).
  useEffect(() => {
    if (isFollowingUser && !useMapStore.getState().isTrackingLocation) {
      geoRef.current.startTracking();
    }
  }, [isFollowingUser]);

  // Recentre on every fix while following (outside turn-by-turn nav).
  useEffect(() => {
    if (!isFollowingUser || navActive || !storeMap || !followUserLocation) return;
    storeMap.easeTo({
      center: [followUserLocation.lng, followUserLocation.lat],
      duration: 700,
    });
  }, [followUserLocation, isFollowingUser, navActive, storeMap]);

  // A manual pan/rotate disengages follow (programmatic easeTo carries no
  // originalEvent, so our own recentres never trip this).
  useEffect(() => {
    if (!storeMap) return;
    const onGesture = (e: { originalEvent?: unknown }) => {
      if (e.originalEvent) useMapStore.getState().setIsFollowingUser(false);
    };
    storeMap.on('dragstart', onGesture);
    storeMap.on('rotatestart', onGesture);
    return () => {
      storeMap.off('dragstart', onGesture);
      storeMap.off('rotatestart', onGesture);
    };
  }, [storeMap]);

  // Show a crosshair cursor while a directions field OR the isochrone explorer is
  // awaiting a map click.
  const isoPicking = useIsochroneStore((s) => s.active && s.picking);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.getCanvas().style.cursor = activeWaypointId || isoPicking ? 'crosshair' : '';
  }, [activeWaypointId, isoPicking]);

  const handleMapMove = useCallback(() => {
    if (!mapRef.current) return;

    const center = mapRef.current.getCenter();
    const zoom = mapRef.current.getZoom();
    const bearing = mapRef.current.getBearing();
    const pitch = mapRef.current.getPitch();

    setViewport({
      center: { lng: center.lng, lat: center.lat },
      zoom,
      bearing,
      pitch,
    });
  }, [setViewport]);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = new MapLibreMap({
      container: mapContainerRef.current,
      style: DEFAULT_STYLE,
      center: [viewport.center.lng, viewport.center.lat],
      zoom: viewport.zoom,
      bearing: viewport.bearing ?? 0,
      pitch: viewport.pitch ?? 0,
      maxZoom: 20,
      minZoom: 1,
      attributionControl: false,
      antialias: true,
    });

    // Add navigation control
    map.addControl(
      new NavigationControl({
        visualizePitch: true,
        showCompass: true,
        showZoom: true,
      }),
      'bottom-right'
    );

    // Add scale control
    map.addControl(
      new ScaleControl({
        maxWidth: 100,
        unit: preferences.unitSystem === 'imperial' ? 'imperial' : 'metric',
      }),
      'bottom-left'
    );

    // Add attribution control. Credits OpenMapTiles (CC-BY 4.0, schema/design)
    // and OpenStreetMap (ODbL, data) — both legally required. The string matches
    // the style source `attribution` so MapLibre dedupes to a single entry.
    map.addControl(
      new maplibregl.AttributionControl({
        compact: true,
        customAttribution: '&copy; <a href="https://www.openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors | <a href="/attribution.html" target="_blank">Attributions</a>',
      }),
      'bottom-left'
    );

    // Re-apply programmatic layers/terrain on every style load — including after
    // a base-style switch (map.setStyle drops custom layers/sources). Reads
    // current flags from the store so the handler is never stale.
    map.on('style.load', () => {
      const st = useMapStore.getState();
      if (st.show3DBuildings) add3DBuildings(map);
      if (config.features.terrain && st.showTerrain) addTerrain(map);
    });

    map.once('load', () => {
      setIsLoaded(true);

      // Location tracking is now handled by useGeolocation hook
    });

    map.on('moveend', handleMapMove);
    map.on('zoomend', handleMapMove);

    // Right-click context menu (Directions from/to here, What's here?)
    map.on('contextmenu', (e) => {
      setCtxMenu({ x: e.point.x, y: e.point.y, lat: e.lngLat.lat, lng: e.lngLat.lng });
    });
    map.on('movestart', () => setCtxMenu(null));

    map.on('click', (e) => {
      setCtxMenu(null);
      const routing = useRoutingStore.getState();

      // 0) Isochrone: when the "reachable area" explorer is picking an origin,
      // the click sets the start point and consumes the click so it doesn't also
      // open a POI card. Directions still takes priority below.
      const iso = useIsochroneStore.getState();
      if (iso.active && iso.picking && !routing.activeWaypointId) {
        iso.setOrigin({ lat: e.lngLat.lat, lng: e.lngLat.lng });
        return;
      }

      // 1) Directions: when a waypoint field is focused, the click fills it.
      if (routing.activeWaypointId) {
        const coords = { lat: e.lngLat.lat, lng: e.lngLat.lng };
        const wpId = routing.activeWaypointId;
        routing.setWaypoint(wpId, coords, `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`);
        routing.setActiveWaypointId(null);
        searchService
          .reverseGeocode(coords)
          .then((result) => {
            if (result?.address?.formatted) {
              useRoutingStore.getState().setWaypoint(wpId, coords, result.address.formatted);
            }
          })
          .catch(() => {/* keep coordinate fallback */});
        return;
      }

      // 2) POI click: open its place card. POIs live in the vector tiles'
      // "poi" source-layer; query a small box around the click for tolerance.
      const pad = 6;
      const poi = map
        .queryRenderedFeatures([
          [e.point.x - pad, e.point.y - pad],
          [e.point.x + pad, e.point.y + pad],
        ])
        .find((f) => f.sourceLayer === 'poi' && f.properties && f.properties.name);
      if (!poi) return;

      const geom = poi.geometry as GeoJSON.Geometry;
      const coords =
        geom.type === 'Point'
          ? { lng: (geom.coordinates as number[])[0], lat: (geom.coordinates as number[])[1] }
          : { lng: e.lngLat.lng, lat: e.lngLat.lat };
      const name = String(poi.properties!.name);
      const result: SearchResult = {
        id: poi.id != null ? `osm/${poi.id}` : `poi/${coords.lat.toFixed(5)},${coords.lng.toFixed(5)}`,
        name,
        displayName: name,
        category: String(poi.properties!.subclass || poi.properties!.class || 'other') as PlaceCategory,
        coordinates: coords,
        address: { formatted: name } as Address,
        relevanceScore: 1,
      };

      const search = useSearchStore.getState();
      search.setSelectedPlace(null);
      search.setSelectedResult(result);
      useUIStore.getState().openPanel('placeDetailsPanel');

      // Enrich the card with a real reverse-geocoded address. Guard against a
      // stale response overwriting a newer selection (user clicked another POI).
      searchService
        .reverseGeocode(coords)
        .then((r) => {
          const current = useSearchStore.getState().selectedResult;
          if (r?.address && current && current.id === result.id) {
            useSearchStore.getState().setSelectedResult({
              ...result,
              address: r.address,
              displayName: r.address.formatted || name,
            });
          }
        })
        .catch(() => {/* keep POI name */});
    });

    mapRef.current = map;
    setMap(map);

    return () => {
      map.remove();
      mapRef.current = null;
      setMap(null);
    };
  }, []);

  // Handle 3D buildings toggle
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    if (show3DBuildings) {
      add3DBuildings(map);
    } else {
      if (map.getLayer('3d-buildings')) {
        map.removeLayer('3d-buildings');
      }
    }
  }, [show3DBuildings]);

  // Handle terrain toggle
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    if (config.features.terrain && showTerrain) {
      addTerrain(map);
    } else {
      map.setTerrain(null);
    }
  }, [showTerrain]);

  return (
    <div className="relative w-full h-full">
      <div ref={mapContainerRef} className="w-full h-full" />
      {ctxMenu && (
        <MapContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          lat={ctxMenu.lat}
          lng={ctxMenu.lng}
          onClose={() => setCtxMenu(null)}
        />
      )}
      <MapControls />
      <ShareLayer />
      <MapLayers />
      <MapMarkers />
      <TrafficLayer />
      <IsochroneLayer />
      <ReportHazard />
      <GeoChat />
      <LocationPanel />
      <LocationPermissionPrompt />
    </div>
  );
}

function add3DBuildings(map: MapLibreMap) {
  if (map.getLayer('3d-buildings')) return;

  const layers = map.getStyle().layers;
  const labelLayerId = layers?.find(
    (layer) => layer.type === 'symbol' && layer.layout?.['text-field']
  )?.id;

  map.addLayer(
    {
      id: '3d-buildings',
      source: 'openmaptiles',
      'source-layer': 'building',
      type: 'fill-extrusion',
      minzoom: 14,
      paint: {
        'fill-extrusion-color': [
          'interpolate',
          ['linear'],
          ['get', 'render_height'],
          0, '#e8eaed',
          50, '#dadce0',
          100, '#bdc1c6',
          200, '#9aa0a6',
        ],
        'fill-extrusion-height': [
          'interpolate',
          ['linear'],
          ['zoom'],
          14, 0,
          14.5, ['get', 'render_height'],
        ],
        'fill-extrusion-base': [
          'interpolate',
          ['linear'],
          ['zoom'],
          14, 0,
          14.5, ['get', 'render_min_height'],
        ],
        'fill-extrusion-opacity': 0.8,
      },
    },
    labelLayerId
  );
}

function addTerrain(map: MapLibreMap) {
  if (!map.getSource('terrain')) {
    map.addSource('terrain', {
      type: 'raster-dem',
      url: '/api/styles/terrain-dem.json',
      tileSize: 256,
    });
  }

  map.setTerrain({ source: 'terrain', exaggeration: 1.5 });
}
